import React, { useState, useEffect } from 'react';
import logo from '../assets/logo/logo.png';
import { motion } from 'framer-motion';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import {
  FiInstagram, FiTwitter, FiFacebook,
  FiYoutube, FiLinkedin, FiArrowUp, FiGlobe,
} from 'react-icons/fi';
import { FaWhatsapp, FaImdb, FaVimeo, FaTumblr } from 'react-icons/fa';

const footerLinks = {
  Company: ['About Us', 'Our Team', 'Awards', 'Press & Media', 'Careers'],
  Productions: ['All Films', 'Upcoming', 'Documentaries', 'Short Films', 'Web Series'],
  Services: ['Film Production', 'Direction', 'VFX & Post', 'Music & Score', 'Distribution'],
  Connect: ['Contact Us', 'Partnerships', 'Casting Calls', 'Submissions', 'Newsletter'],
};

const socials = [
  { icon: FiGlobe, href: 'https://magicmoonstudios.com/', label: 'Website' },
  { icon: FiInstagram, href: 'https://www.instagram.com/magicmoonstudios/', label: 'Instagram' },
  { icon: FiLinkedin, href: 'https://in.linkedin.com/in/magicmoon-studios-41a74a323', label: 'LinkedIn' },
  { icon: FaTumblr, href: 'https://www.tumblr.com/magicmoonstudios', label: 'Tumblr' },
  { icon: FiYoutube, href: 'https://www.youtube.com/@magicmoonstudios', label: 'YouTube' },
  { icon: FiTwitter, href: '#', label: 'Twitter' },
  { icon: FiFacebook, href: 'https://www.facebook.com/MagicMoonProduction/', label: 'Facebook' },
  { icon: FaWhatsapp, href: '#', label: 'WhatsApp' },
  { icon: FaImdb, href: '#', label: 'IMDb' },
  { icon: FaVimeo, href: '#', label: 'Vimeo' },
];

export default function Footer() {
  const [visitors, setVisitors] = useState(0);

  useEffect(() => {
    const statsRef = doc(db, 'stats', 'visitors');
    const unsubscribe = onSnapshot(statsRef, (docSnap) => {
      if (docSnap.exists()) {
        setVisitors(docSnap.data().count || 0);
      }
    });
    return () => unsubscribe();
  }, []);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer className="relative bg-brand-black border-t border-brand-gold/10 overflow-hidden">
      {/* Decorative top glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-px bg-gradient-to-r from-transparent via-brand-gold/50 to-transparent" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-16 bg-brand-orange/5 blur-2xl" />

      <div className="max-w-7xl mx-auto px-6 pt-20 pb-10">
        {/* Top grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 mb-16">
          {/* Brand col */}
          <div className="lg:col-span-2">
            {/* Logo */}
            <div className="mb-6">
              <img
                src={logo}
                alt="Magic Moon Studios"
                className="h-14 w-auto object-contain"
                style={{ filter: 'drop-shadow(0 0 10px rgba(243,112,33,0.35))' }}
              />
            </div>

            <p className="font-inter text-white/40 text-sm leading-relaxed mb-6 max-w-xs">
              Crafting cinematic legends that move the world. Where every story
              is told with passion, precision and unmatched grandeur.
            </p>

            {/* Social icons */}
            <div className="flex flex-wrap gap-3">
              {socials.map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.a
                    key={i}
                    href={s.href}
                    aria-label={s.label}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.15, y: -2 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-9 h-9 rounded-sm glass-card flex items-center justify-center text-white/50 hover:text-white transition-colors duration-300"
                  >
                    <Icon size={15} />
                  </motion.a>
                );
              })}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title} className="lg:col-span-1">
              <h4 className="font-cinzel font-bold text-brand-gold/80 text-xs tracking-widest uppercase mb-5">
                {title}
              </h4>
              <ul className="flex flex-col gap-3">
                {links.map((link) => (
                  <li key={link}>
                    <a
                      href="#"
                      className="font-inter text-white/40 text-sm hover:text-brand-orange transition-colors duration-300 leading-none"
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-brand-gold/15 to-transparent mb-8" />

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col gap-1 text-center md:text-left">
            <p className="font-inter text-white/25 text-xs">
              &copy; {new Date().getFullYear()} Magic Moon Studios. All rights reserved. Crafted with passion for cinema.
            </p>
            <p className="font-inter text-brand-gold text-xs font-bold tracking-widest uppercase mt-1">
              Total Visitors: {visitors.toLocaleString()}
            </p>
            <p className="font-inter text-white/20 text-[10px] mt-1">
              Designed and developed by{' '}
              <a href="https://www.qubright.in" target="_blank" rel="noopener noreferrer" className="hover:text-brand-gold transition-colors">
                Qubright Technologies LLP
              </a>
            </p>
          </div>

          <div className="flex items-center gap-6">
            {[
              { label: 'Privacy Policy', href: '#/privacy' },
              { label: 'Terms of Use', href: '#/terms' },
              { label: 'Cookies', href: '#/cookies' },
            ].map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="font-inter text-white/25 text-xs hover:text-brand-gold/60 transition-colors duration-300"
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* Back to top */}
          <motion.button
            onClick={scrollTop}
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.9 }}
            className="w-10 h-10 rounded-sm flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, #f37021, #D4AF37)',
              boxShadow: '0 4px 20px rgba(243,112,33,0.4)',
            }}
            aria-label="Back to top"
          >
            <FiArrowUp size={16} color="#0a0a0a" />
          </motion.button>
        </div>
      </div>
    </footer>
  );
}
